import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchUsers } from '../../../Profile/redux/thunks/userThunks';
import { fetchMedicalInfoByUserId } from '../../../Profile/redux/thunks/medicalThunks';
import { fetchEmergencyContacts } from '../../../Profile/redux/thunks/emergencyContactThunks';
import './PatientRecord.css';

import printIcon from '../../../assets/icons/Print.png';
import plusIcon from '../../../assets/icons/plus.png';

import Insurance from "../../components/ProfileDoctorPage/Insurance/Insurance";
import EmergencyContact from '../../../Profile/components/ProfilePage/Emergencycontact/EmergencyContact';
import PatientCard from '../../components/ProfileDoctorPage/PatientCard/PatientCard';
import GeneralInformations from '../../components/ProfileDoctorPage/GeneralInformations/GeneralInformations';
import TestReports from '../../components/ProfileDoctorPage/TestReports/TestReports';
import Previous from '../../components/ProfileDoctorPage/Previous/Previous';
import Allergies from '../../../Profile/components/OverViewsPage/Allergies/Allergies';
import Past from '../../components/ProfileDoctorPage/Past/Past';
import AddNewConsultation from '../../../consultation/components/FilePage/AddNewConsultationZone/AddNewConsultationZone';
import FollowUpVisitPopup from '../../components/AddNewConsultationPopup/FollowUpVisitPopup/FollowUpVisitPopup';
import SuccessNotification from '../../components/AddNewConsultationPopup/SuccessNotification/SuccessNotification';
import AppLayoutDoctor from '../../../navigation/Layout/LayoutDoctor';

const PatientRecord = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  
  // ✅ Récupération des données user, medical et emergency depuis Redux
const patients = useSelector((state) => state.user.list);
const usersLoading = useSelector((state) => state.user.loading);
const usersError = useSelector((state) => state.user.error);
  const { data: medicalData, loading: medicalLoading, error: medicalError } = useSelector((state) => state.medical);
  const emergencyState = useSelector((state) => state.emergencyContacts || {});

  const [activeTab] = useState('profile');
  const [isConsultationPopupOpen, setIsConsultationPopupOpen] = useState(false);
  const [isFollowUpPopupOpen, setIsFollowUpPopupOpen] = useState(false);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);

  const handleOpenConsultationPopup = () => setIsConsultationPopupOpen(true);
  const handleCloseConsultationPopup = () => setIsConsultationPopupOpen(false);

  const handlePrintRecordFromConsultation = () => {
    setIsConsultationPopupOpen(false);
    setIsFollowUpPopupOpen(true);
    setShowSuccessNotification(true);
    setTimeout(() => setShowSuccessNotification(false), 5000);
  };

  const handlePrintPatientRecord = () => {
    const printDate = new Date().toLocaleDateString('fr-FR', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
    const container = document.querySelector('.patient-record-container');
    if (container) container.setAttribute('data-print-date', printDate);
    setTimeout(() => window.print(), 100);
  };

  const handleCloseFollowUpPopup = () => setIsFollowUpPopupOpen(false);
  const handleRecommendFollowUp = () => undefined;

  // ✅ Fetch patients si non chargés
  useEffect(() => {
    if (!patients || patients.length === 0) {
      dispatch(fetchUsers());
    }
  }, [dispatch, patients]);

  // ✅ Fetch medical info (incluant assurance) quand l'ID patient change
  useEffect(() => {
    if (id) {
      dispatch(fetchMedicalInfoByUserId(id));
    }
  }, [dispatch, id]);

  // ✅ Fetch emergency contacts quand l'ID patient change
  useEffect(() => {
    if (id) {
      dispatch(fetchEmergencyContacts({ userId: id }));
    }
  }, [dispatch, id]);

  // ✅ Trouver le patient par ID
  const patientId = patients?.find((p) => p.id.toString() === id) || null;

  // ✅ Extraire la liste des assurances depuis medicalData
  const insuranceList = Array.isArray(medicalData?.insurance) ? medicalData.insurance : [];
  const nomMutuelle = medicalData?.nomMutuelle;

  // ✅ Extraire les contacts d'urgence depuis Redux (byId/allIds)
  const { byId = {}, allIds = [], isLoading: emergencyLoading } = emergencyState;
  const emergencyContacts = allIds.map((contactId) => byId[contactId]).filter(Boolean);
  
  // ✅ Prendre le premier contact d'urgence (ou null si aucun)
  const emergencyData = emergencyContacts.length > 0 ? emergencyContacts[0] : null;


  // Gestion du chargement
  if (usersLoading || medicalLoading || emergencyLoading) {
    return (
      <AppLayoutDoctor>
        <div className="patient-record-container">
          <p>Chargement des données du patient...</p>
        </div>
      </AppLayoutDoctor>
    );
  }

  // Gestion des erreurs
  if (usersError) return <p>❌ Erreur utilisateur: {usersError}</p>;
  if (medicalError) return <p>❌ Erreur données médicales: {medicalError}</p>;
  if (!patientId) return <p>Patient introuvable.</p>;

  // Données de test (à remplacer par des vraies données si disponibles)
  const testData = {
    testName: 'Blood Test Results',
    date: '20 August 2025',
    fileSize: '4 MB'
  };

  return (
    <AppLayoutDoctor>
      <div className="patient-record-container">
        {/* Header */}
        <div className="header-content">
          <div className="patient-info-section">
            <h2>Patient Record</h2>
            <p>All essential patient information in one place</p>
          </div>
          <div className="header-actions">
            <button className="add-consultation-btn" onClick={handleOpenConsultationPopup}>
              <img src={plusIcon} alt="Plus" className="plus-icon" />
              <span className="btn-text">Add New Consultation</span>
            </button>
            <button className="print-record-btn" onClick={handlePrintPatientRecord}>
              <img src={printIcon} alt="Print" className="print-icon" />
              <span className="btn-text">Print Record</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="patient-record-content">
          {/* Left Section */}
          <div className="patient-profile-section">
            <PatientCard patientId={patientId} />
            <GeneralInformations patientId={patientId} />
            {/* ✅ Affichage dynamique de la liste des assurances depuis medicalData */}
            <Insurance insuranceList={insuranceList} nomMutuelle={nomMutuelle} />
            {/* ✅ Utilisation des vraies données de contact d'urgence depuis Redux */}
            <EmergencyContact emergencyData={emergencyData} />
          </div>

          {/* Right Section */}
          <div className="medical-reports-section">
            <TestReports testData={testData} />
            <Previous />
            <div className="allergies-past-container">
              <Allergies />
              <Past />
            </div>
          </div>
        </div>

        {/* Popups */}
        <AddNewConsultation
          isOpen={isConsultationPopupOpen}
          onClose={handleCloseConsultationPopup}
          onPrintRecord={handlePrintRecordFromConsultation}
          patientId={patientId}
          insuranceList={insuranceList}
          emergencyData={emergencyData}
        />

        <FollowUpVisitPopup
          isOpen={isFollowUpPopupOpen}
          onClose={handleCloseFollowUpPopup}
          onRecommendFollowUp={handleRecommendFollowUp}
        />

        <SuccessNotification isVisible={showSuccessNotification} />
      </div>
    </AppLayoutDoctor>
  );
};

export default PatientRecord;

